/**
 * KioskMode - High-velocity fullscreen QR scanner for venue entry
 * Designed for tablets at venue doors with rapid check-in capability
 * Supports both camera scanning and manual input
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ScanLine, CheckCircle2, XCircle, AlertCircle, Maximize,
  Volume2, VolumeX, X, Users, Zap, Printer, Camera, Keyboard
} from 'lucide-react';
import { checkinAPI } from '@/lib/api';
import { Scanner } from '@yudiel/react-qr-scanner';

const KioskMode = ({ eventId, eventName, onExit }) => {
  const [qrInput, setQrInput] = useState('');
  const [result, setResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoPrint, setAutoPrint] = useState(false);
  const [useCameraScanner, setUseCameraScanner] = useState(true); // Camera vs manual input
  const [stats, setStats] = useState({ total: 0, success: 0, denied: 0 });
  const [recentScans, setRecentScans] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastScannedCode, setLastScannedCode] = useState(''); // Prevent duplicate scans
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const printFrameRef = useRef(null);
  const successAudioRef = useRef(null);
  const errorAudioRef = useRef(null);

  // Auto-focus input for barcode scanner
  useEffect(() => {
    inputRef.current?.focus();
    const interval = setInterval(() => {
      if (document.activeElement !== inputRef.current) {
        inputRef.current?.focus();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Create audio elements for feedback
  useEffect(() => {
    successAudioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJOLjHptU1hwg4qMfXBVYHuJjo98b1lkeI2RjX5wWGV6jpGNfm9YZXqOkY1+b1hleo6RjX5wWGV6jpGNfm9YZQ==');
    errorAudioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJiQjXZqUl52iZKPgHJYX3mMk499cFdheoyTj31wV2F6jJOPfXBXYXqMk499cFdheoyTj31wV2F6jJOPfXBXYXqMk499cFdheoyTj31wV2E=');
  }, []);

  // Badge print function
  const triggerBadgePrint = useCallback((delegate) => {
    if (!autoPrint) return;
    
    // Create a hidden iframe for printing
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            @page { size: 54mm 86mm; margin: 0; }
            body { margin: 0; padding: 4mm; font-family: Arial, sans-serif; }
            .badge { width: 46mm; height: 78mm; border: 1px solid #000; padding: 3mm; box-sizing: border-box; }
            .name { font-size: 14pt; font-weight: bold; text-align: center; margin-top: 10mm; }
            .company { font-size: 10pt; text-align: center; color: #666; }
            .tier { font-size: 8pt; text-align: center; margin-top: 5mm; padding: 2mm 4mm; background: #000; color: #fff; display: inline-block; }
            .qr { text-align: center; margin-top: 5mm; }
            .qr img { width: 25mm; height: 25mm; }
          </style>
        </head>
        <body>
          <div class="badge">
            <div class="name">${delegate.name}</div>
            <div class="company">${delegate.company || ''}</div>
            <div style="text-align: center;"><span class="tier">${delegate.tier || 'ATTENDEE'}</span></div>
            <div class="qr">
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${delegate.id}" />
            </div>
          </div>
        </body>
      </html>
    `;
    
    // Use iframe for silent printing
    if (printFrameRef.current) {
      printFrameRef.current.remove();
    }
    
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
    printFrameRef.current = iframe;
    
    iframe.contentDocument.write(printContent);
    iframe.contentDocument.close();
    
    // Trigger print after content loads
    setTimeout(() => {
      iframe.contentWindow.print();
    }, 500);
  }, [autoPrint]);

  const playSound = useCallback((success) => {
    if (!soundEnabled) return;
    const audio = success ? successAudioRef.current : errorAudioRef.current;
    audio?.play().catch(() => {});
  }, [soundEnabled]);

  const handleScan = useCallback(async (qrData) => {
    if (!qrData?.trim() || isProcessing) return;
    
    setIsProcessing(true);
    setResult(null);
    
    try {
      const response = await checkinAPI.checkIn(eventId, qrData.trim());
      const data = response.data;
      
      setResult(data);
      setStats(prev => ({
        total: prev.total + 1,
        success: data.status === 'authorized' ? prev.success + 1 : prev.success,
        denied: data.status !== 'authorized' ? prev.denied + 1 : prev.denied
      }));
      
      // Add to recent scans
      setRecentScans(prev => [{
        ...data,
        timestamp: new Date().toLocaleTimeString()
      }, ...prev.slice(0, 9)]);
      
      playSound(data.status === 'authorized');
      
      // Trigger badge print on successful check-in (if enabled)
      if (data.status === 'authorized' && data.delegate) {
        triggerBadgePrint(data.delegate);
      }
      
      // Auto-clear after 3 seconds
      setTimeout(() => {
        setResult(null);
        setQrInput('');
        inputRef.current?.focus();
      }, 3000);
      
    } catch (err) {
      const errorResult = {
        status: 'error',
        reason: err.response?.data?.detail || 'Check-in failed'
      };
      setResult(errorResult);
      setStats(prev => ({ ...prev, total: prev.total + 1, denied: prev.denied + 1 }));
      playSound(false);
      
      setTimeout(() => {
        setResult(null);
        setQrInput('');
        inputRef.current?.focus();
      }, 3000);
    } finally {
      setIsProcessing(false);
    }
  }, [eventId, isProcessing, playSound, triggerBadgePrint]);

  // Handle QR code from camera scanner
  const handleCameraScan = useCallback((detectedCodes) => {
    if (!detectedCodes || detectedCodes.length === 0 || isProcessing) return;
    
    const code = detectedCodes[0]?.rawValue;
    if (!code || code === lastScannedCode) return;
    
    // Debounce: prevent same code within 3 seconds
    setLastScannedCode(code);
    setTimeout(() => setLastScannedCode(''), 3000);
    
    handleScan(code);
  }, [isProcessing, lastScannedCode, handleScan]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleScan(qrInput);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Determine background color based on result
  const getBgColor = () => {
    if (!result) return 'bg-slate-900';
    if (result.status === 'authorized') return 'bg-emerald-900';
    if (result.status === 'already_checked_in') return 'bg-amber-900';
    return 'bg-rose-900';
  };

  return (
    <div 
      ref={containerRef}
      className={`min-h-screen ${getBgColor()} transition-colors duration-500 flex flex-col`}
      data-testid="kiosk-mode"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/30">
        <div className="flex items-center gap-4">
          <Badge variant="outline" className="text-white border-white/30 text-lg px-4 py-2">
            {eventName || 'Event Check-In'}
          </Badge>
          <Badge variant="secondary" className="text-lg px-4 py-2">
            <Users className="h-4 w-4 mr-2" />
            {stats.success} checked in
          </Badge>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Camera/Manual Toggle */}
          <Button
            variant="ghost"
            size="icon"
            className={`text-white hover:bg-white/20 ${useCameraScanner ? 'bg-blue-500/30' : ''}`}
            onClick={() => setUseCameraScanner(!useCameraScanner)}
            title={useCameraScanner ? 'Using Camera' : 'Using Manual Input'}
          >
            {useCameraScanner ? <Camera className="h-6 w-6 text-blue-400" /> : <Keyboard className="h-6 w-6" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={`text-white hover:bg-white/20 ${autoPrint ? 'bg-emerald-500/30' : ''}`}
            onClick={() => setAutoPrint(!autoPrint)}
            title={autoPrint ? 'Auto-print ON' : 'Auto-print OFF'}
          >
            <Printer className={`h-6 w-6 ${autoPrint ? 'text-emerald-400' : ''}`} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20"
            onClick={() => setSoundEnabled(!soundEnabled)}
          >
            {soundEnabled ? <Volume2 className="h-6 w-6" /> : <VolumeX className="h-6 w-6" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20"
            onClick={toggleFullscreen}
          >
            <Maximize className="h-6 w-6" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20"
            onClick={onExit}
          >
            <X className="h-6 w-6" />
          </Button>
        </div>
      </div>

      {/* Main Scanner Area */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-2xl">
          {/* Result Display */}
          {result ? (
            <div className="text-center animate-in fade-in zoom-in duration-300">
              {result.status === 'authorized' ? (
                <>
                  <CheckCircle2 className="h-48 w-48 text-emerald-400 mx-auto mb-8 animate-pulse" />
                  <h1 className="text-6xl font-bold text-white mb-4 tracking-wider">AUTHORIZED</h1>
                  <p className="text-4xl text-emerald-200 mb-2">{result.delegate?.name}</p>
                  <p className="text-2xl text-emerald-300/80">{result.delegate?.company}</p>
                  <Badge className="mt-4 text-xl px-6 py-2" variant="secondary">
                    {result.delegate?.tier}
                  </Badge>
                </>
              ) : result.status === 'already_checked_in' ? (
                <>
                  <AlertCircle className="h-48 w-48 text-amber-400 mx-auto mb-8" />
                  <h1 className="text-6xl font-bold text-white mb-4 tracking-wider">DUPLICATE</h1>
                  <p className="text-3xl text-amber-200">{result.delegate?.name}</p>
                  <p className="text-xl text-amber-300/80 mt-2">Already checked in</p>
                </>
              ) : (
                <>
                  <XCircle className="h-48 w-48 text-rose-400 mx-auto mb-8" />
                  <h1 className="text-6xl font-bold text-white mb-4 tracking-wider">DENIED</h1>
                  <p className="text-2xl text-rose-200">{result.reason}</p>
                </>
              )}
            </div>
          ) : (
            <div className="text-center">
              {useCameraScanner ? (
                /* Camera Scanner */
                <>
                  <div className="relative w-80 h-80 mx-auto mb-6 rounded-2xl overflow-hidden border-4 border-white/30">
                    <Scanner
                      onScan={handleCameraScan}
                      onError={(error) => console.log('Scanner error:', error)}
                      constraints={{ facingMode: 'environment' }}
                      scanDelay={500}
                      styles={{
                        container: { width: '100%', height: '100%' },
                        video: { width: '100%', height: '100%', objectFit: 'cover' }
                      }}
                    />
                    {/* Scanning overlay */}
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute inset-8 border-2 border-white/50 rounded-lg" />
                      <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-blue-500 animate-pulse" />
                    </div>
                  </div>
                  <h1 className="text-3xl font-bold text-white mb-2">Point Camera at QR Code</h1>
                  <p className="text-lg text-white/60 mb-4">Scanning automatically...</p>
                  <Button
                    variant="outline"
                    className="text-white border-white/30 hover:bg-white/10"
                    onClick={() => setUseCameraScanner(false)}
                  >
                    <Keyboard className="h-4 w-4 mr-2" />
                    Switch to Manual Input
                  </Button>
                </>
              ) : (
                /* Manual Input */
                <>
                  <ScanLine className="h-32 w-32 text-white/50 mx-auto mb-8 animate-pulse" />
                  <h1 className="text-4xl font-bold text-white mb-4">Ready to Scan</h1>
                  <p className="text-xl text-white/60 mb-8">Scan badge QR code or enter ID</p>
                  
                  <Input
                    ref={inputRef}
                    value={qrInput}
                    onChange={(e) => setQrInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="text-2xl text-center h-16 bg-white/10 border-white/30 text-white placeholder-white/40 max-w-md mx-auto"
                    placeholder="Scan or type ID..."
                    autoFocus
                    data-testid="kiosk-input"
                  />
                  
                  <div className="flex gap-4 justify-center mt-4">
                    <Button
                      onClick={() => handleScan(qrInput)}
                      disabled={isProcessing || !qrInput.trim()}
                      className="text-xl px-8 py-6 h-auto"
                      size="lg"
                    >
                      {isProcessing ? (
                        <span className="animate-pulse">Processing...</span>
                      ) : (
                        <>
                          <Zap className="h-6 w-6 mr-2" />
                          Check In
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      className="text-white border-white/30 hover:bg-white/10 px-6 py-6 h-auto"
                      onClick={() => setUseCameraScanner(true)}
                    >
                      <Camera className="h-5 w-5 mr-2" />
                      Use Camera
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats Footer */}
      <div className="p-4 bg-black/30">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center gap-8 text-white/80">
            <div className="text-center">
              <div className="text-3xl font-bold">{stats.total}</div>
              <div className="text-sm">Total Scans</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-400">{stats.success}</div>
              <div className="text-sm">Admitted</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-rose-400">{stats.denied}</div>
              <div className="text-sm">Denied</div>
            </div>
          </div>
          
          {/* Recent scans mini-list */}
          <div className="flex gap-2">
            {recentScans.slice(0, 5).map((scan, i) => (
              <div
                key={i}
                className={`w-3 h-3 rounded-full ${
                  scan.status === 'authorized' ? 'bg-emerald-500' :
                  scan.status === 'already_checked_in' ? 'bg-amber-500' : 'bg-rose-500'
                }`}
                title={`${scan.delegate?.name || 'Unknown'} - ${scan.timestamp}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KioskMode;
